import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {usePricingCurrencies} from "@/features/currencies";
import {Button, Col, Row, Select, Space, Tooltip} from "antd";
import {DeleteOutlined, PlusCircleFilled} from "@ant-design/icons";
import {CustomInputComponent} from "../../../../input/input-component";
import {useCurrencyOptions} from "../../../general/pricing/hooks/currency";
import {StoreServicePricingFormFieldsType} from "../types";
import {StoreServiceCurrenciesFormFieldProps} from "./types";
import {FormFieldError} from "../../../../../design-system/form-field";
import {AutomaticCurrencyConversionNotice} from "../../../../../components/shared/automatic-currency-conversion";

export function ServiceCurrenciesFormField({
  id,
  label,
  placeholder,
}: StoreServiceCurrenciesFormFieldProps) {
  const pricingCurrencies = usePricingCurrencies();
  const {
    control,
    watch,
    formState: {errors},
  } = useFormContext<StoreServicePricingFormFieldsType>();

  const {
    fields: currencyFields,
    append: appendCurrency,
    remove: removeCurrency,
  } = useFieldArray({
    control,
    name: "currencies",
  });
  // console.log({currencyContext});
  const currencyArrayValue = watch("currencies");
  const defaultCallCurrency =
    watch("currencies").length > 0
      ? watch("currencies")[0].callPriceCurrency
      : pricingCurrencies.formCurrencies[0];

  const {
    currencyOptions,
    handleCurrencySelectChange,
    removeCurrency: handleRemoveCurrency,
    addNewCurrency,
  } = useCurrencyOptions(
    currencyArrayValue,
    defaultCallCurrency,
    appendCurrency,
    removeCurrency
  );

  const canAddCurrencies =
    watch("priceType") === "paid" &&
    pricingCurrencies.connectedBankStatus.isBankConnected;
  return (
    <div>
      {canAddCurrencies && (
        <Space direction="vertical" size={24} style={{display: "flex"}}>
          <Row gutter={[10, 24]}>
            {currencyFields.map((item, index) => (
              <Col key={item.id} span={24} sm={12}>
                <div style={{display: "flex", gap: "1.6rem"}}>
                  <div style={{flex: "1"}}>
                    <CustomInputComponent
                      id="callPrice"
                      name={`currencies.${index}.callPrice`}
                      type="text"
                      label={index === 0 ? "Default price" : label}
                      placeholder={placeholder ?? ""}
                      error={
                        errors.currencies?.[index]?.callPriceCurrency
                          ? errors.currencies?.[index]?.callPriceCurrency?.message
                          : errors.currencies?.[index]?.callPrice?.message
                      }
                      control={control}
                      addonBefore={
                        <Controller
                          control={control}
                          name={`currencies.${index}.callPriceCurrency`}
                          render={({field: {onChange, value, ref}}) => (
                            <Space
                              size={16}
                              direction="vertical"
                              style={{display: "flex"}}
                            >
                              <Select
                                onChange={(val) =>
                                  handleCurrencySelectChange(val, onChange)
                                }
                                value={value}
                                ref={ref}
                                size="large"
                                status={
                                  errors.currencies?.[index]?.callPriceCurrency?.message
                                    ? "error"
                                    : ""
                                }
                                showSearch={false}
                                notFoundContent={null}
                                placeholder={""}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  (option!.children as unknown as string)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              >
                                {currencyOptions.map(
                                  (option, optionIdx: number | string) => (
                                    <Select.Option key={optionIdx} value={option}>
                                      {option}
                                    </Select.Option>
                                  )
                                )}
                              </Select>
                              <FormFieldError
                                errorMessage={
                                  errors.currencies?.[index]?.callPriceCurrency?.message
                                }
                              />
                            </Space>
                          )}
                        />
                      }
                    />
                  </div>
                  <div style={{marginTop: "auto"}}>
                    <Tooltip
                      title={
                        currencyFields.length === 1
                          ? "At least one currency is required"
                          : null
                      }
                    >
                      <Button
                        onClick={() => handleRemoveCurrency(index)}
                        type="link"
                        shape="circle"
                        icon={<DeleteOutlined />}
                        size="small"
                        disabled={currencyFields.length === 1}
                      />
                    </Tooltip>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          {watch("priceType") === "paid" && (
            <div>
              <Button
                icon={<PlusCircleFilled />}
                onClick={() => addNewCurrency()}
                size="small"
              >
                Add currency
              </Button>
              <div style={{marginTop: "2.4rem"}}>
                <AutomaticCurrencyConversionNotice sourceCurrency={defaultCallCurrency} />
              </div>
            </div>
          )}
        </Space>
      )}
    </div>
  );
}
